import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSkillsRequestContext } from "@/services/skills";
import {
  applyEngineTask,
  approveEnginePlan,
  cancelEnginePlan,
  clearEngineApply,
  decommissionEngineTask,
  getEngineArtifact,
  getEnginePlanReport,
  isEngineAbsent,
  listEngineArtifacts,
  listEngineEffects,
  listEnginePlans,
  recommissionEngineTask,
  runEnginePlan,
} from "@/services/migrations/engine";
import type {
  EngineApproveRequest,
  EngineArtifactBody,
  EngineArtifactListResponse,
  EngineClearApplyRequest,
  EngineCoverageBucket,
  EngineEffectsPage,
  EnginePlanListResponse,
  EnginePlanReport,
} from "@/services/migrations/engine";

/**
 * React Query wiring for the migrations engine.
 *
 * Three deliberate decisions:
 *
 *  1. AVAILABILITY IS A RUNTIME CHECK. The engine API does not exist in every
 *     deployment. `useEngineAvailable` probes the plans index once; a 404/405/501 means
 *     "no engine here" and the entire surface renders nothing, leaving the existing
 *     playbook migrations UI exactly as it was.
 *
 *  2. THE REPORT IS POLLED, NOT STREAMED. `stores/studio/runStore` treats a dropped SSE
 *     stream as `complete("done")` — benign for a playbook whose result is already
 *     persisted, unacceptable for a run whose entire product is a verdict, because a
 *     cut connection would render as a clean finish. Polling `/report` cannot fabricate
 *     a terminal state: the status it shows is the status the server last returned.
 *
 *  3. EVERY WRITE INVALIDATES THE REPORT, INCLUDING THE ONES THAT FAIL. An apply that
 *     refuses changed nothing; an apply that goes indeterminate changed the task's
 *     status and possibly the org. The screen after any write attempt must be re-read
 *     from the server rather than reasoned about from the response, so `onSettled` —
 *     not `onSuccess` — does the invalidating on the write mutations.
 */

/** Statuses after which the report will not change again, so polling can stop. */
const TERMINAL_PLAN_STATUSES = new Set(["done", "failed", "cancelled", "refused"]);

const PLAN_POLL_MS = 5000;

/** One page of effects. Matches the server's own default (`DEFAULT_PAGE`). */
export const EFFECTS_PAGE_SIZE = 50;

export function engineKeys(orgId: string | null) {
  return {
    available: ["migration-engine", "available", orgId] as const,
    plans: (limit: number, status?: string) =>
      ["migration-engine", "plans", orgId, limit, status ?? null] as const,
    report: (planId: string | undefined) =>
      ["migration-engine", "report", orgId, planId ?? null] as const,
    effects: (planId: string, taskPath: string, bucket: EngineCoverageBucket, offset: number) =>
      ["migration-engine", "effects", orgId, planId, taskPath, bucket, offset] as const,
    artifacts: (planId: string, taskPath: string) =>
      ["migration-engine", "artifacts", orgId, planId, taskPath] as const,
    artifactBody: (planId: string, taskPath: string, componentKey: string, version: number) =>
      [
        "migration-engine",
        "artifact-body",
        orgId,
        planId,
        taskPath,
        componentKey,
        version,
      ] as const,
  };
}

/**
 * Is there an engine behind this deployment? Resolves to `false` rather than throwing
 * when the API is absent — callers render nothing on `false`, so a missing backend is
 * invisible instead of broken.
 */
export function useEngineAvailable() {
  const ctx = useSkillsRequestContext();
  return useQuery<boolean>({
    queryKey: engineKeys(ctx.orgId).available,
    queryFn: async () => {
      try {
        await listEnginePlans({ limit: 1 });
        return true;
      } catch (error) {
        if (isEngineAbsent(error)) return false;
        // Anything else (network down, 500, auth) is also not proof of an engine, and
        // the surface must not half-render against it. Treat as unavailable; the probe
        // re-runs on the next mount.
        return false;
      }
    },
    enabled: Boolean(ctx.orgId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useEnginePlans(opts: { limit?: number; status?: string } = {}) {
  const ctx = useSkillsRequestContext();
  const available = useEngineAvailable();
  const limit = opts.limit ?? 25;
  return useQuery<EnginePlanListResponse>({
    queryKey: engineKeys(ctx.orgId).plans(limit, opts.status),
    queryFn: () => listEnginePlans({ limit, status: opts.status }),
    enabled: Boolean(ctx.orgId) && available.data === true,
    staleTime: 15_000,
    retry: false,
  });
}

export function useEnginePlanReport(planId: string | undefined) {
  const ctx = useSkillsRequestContext();
  const available = useEngineAvailable();
  return useQuery<EnginePlanReport>({
    queryKey: engineKeys(ctx.orgId).report(planId),
    queryFn: () => getEnginePlanReport(planId!),
    enabled: Boolean(planId && ctx.orgId) && available.data === true,
    // Poll until the plan reaches a state that cannot change. No SSE: see the note at
    // the top of this file.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && TERMINAL_PLAN_STATUSES.has(status) ? false : PLAN_POLL_MS;
    },
    staleTime: 0,
    retry: false,
  });
}

/**
 * One page of one coverage bucket.
 *
 * Paged for every bucket, not just `carried`. A caller should not have to learn which
 * lists are big, and "this bucket happens to be small today" is a property of the org
 * someone last looked at rather than of the data.
 */
export function useEngineEffects(
  planId: string | undefined,
  taskPath: string | undefined,
  opts: { bucket: EngineCoverageBucket; limit?: number; offset?: number; enabled?: boolean }
) {
  const ctx = useSkillsRequestContext();
  const offset = opts.offset ?? 0;
  const limit = opts.limit ?? EFFECTS_PAGE_SIZE;
  return useQuery<EngineEffectsPage>({
    queryKey: engineKeys(ctx.orgId).effects(planId ?? "", taskPath ?? "", opts.bucket, offset),
    queryFn: () => listEngineEffects(planId!, taskPath!, { bucket: opts.bucket, limit, offset }),
    enabled: Boolean(planId && taskPath && ctx.orgId) && opts.enabled !== false,
    // The list a reader is walking must not blank out between pages.
    placeholderData: (previous) => previous,
    staleTime: 60_000,
    retry: false,
  });
}

export function useEngineArtifacts(
  planId: string | undefined,
  taskPath: string | undefined,
  enabled = true
) {
  const ctx = useSkillsRequestContext();
  return useQuery<EngineArtifactListResponse>({
    queryKey: engineKeys(ctx.orgId).artifacts(planId ?? "", taskPath ?? ""),
    queryFn: () => listEngineArtifacts(planId!, taskPath!),
    enabled: Boolean(planId && taskPath && ctx.orgId) && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * One artifact's bytes, at an EXPLICIT version.
 *
 * The version is part of the key and part of the request, never left to default. A view
 * that asked for "the latest" would render whatever the server last compiled while
 * claiming, in its own header, to be showing the version the reader selected — and this
 * body is the thing an approval is a statement about.
 */
export function useEngineArtifactBody(
  planId: string | undefined,
  taskPath: string | undefined,
  componentKeyEncoded: string | undefined,
  version: number | undefined,
  enabled = true
) {
  const ctx = useSkillsRequestContext();
  return useQuery<EngineArtifactBody>({
    queryKey: engineKeys(ctx.orgId).artifactBody(
      planId ?? "",
      taskPath ?? "",
      componentKeyEncoded ?? "",
      version ?? 0
    ),
    queryFn: () => getEngineArtifact(planId!, taskPath!, componentKeyEncoded!, version),
    enabled:
      Boolean(planId && taskPath && componentKeyEncoded && version != null && ctx.orgId) && enabled,
    // Artifacts are append-only: version N's bytes never change, so a fetched body stays
    // valid. A new compile is a new version and therefore a new key.
    staleTime: Infinity,
    retry: false,
  });
}

/**
 * Approve or reject. Approval RESUMES a paused plan and records a GATE_APPLY ledger row
 * naming the exact `compile_key` of every task it moves. It does not deploy — the write
 * path reads that row as one of its five factors, and sending anything to an org is a
 * separate, deliberate act.
 */
export function useEngineApproval(planId: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (body: EngineApproveRequest) => approveEnginePlan(planId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
      void queryClient.invalidateQueries({ queryKey: ["migration-engine", "plans", ctx.orgId] });
    },
  });
}

export function useEngineCancel(planId: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: () => cancelEnginePlan(planId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
      void queryClient.invalidateQueries({ queryKey: ["migration-engine", "plans", ctx.orgId] });
    },
  });
}

export function useEngineRun(planId: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: () => runEnginePlan(planId!),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
      void queryClient.invalidateQueries({ queryKey: ["migration-engine", "plans", ctx.orgId] });
    },
  });
}

/**
 * Write the approved component to the org.
 *
 * `onSettled`, not `onSuccess`: an apply that refuses, fails, or goes indeterminate has
 * still moved the task's status, and the only honest next screen is one re-read from the
 * server. An optimistic update here would be a client's opinion about whether a
 * customer's org changed.
 */
export function useEngineApply(planId: string | undefined, taskPath: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (confirmation: string) => applyEngineTask(planId!, taskPath!, confirmation),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
    },
  });
}

/**
 * DEACTIVATE THE SOURCE this migration replaced, or turn it back on.
 *
 * `onSettled`, not `onSuccess`, and here the reason is sharper than it is for an apply: a
 * decommission that goes indeterminate has moved the task's status and may have changed
 * whether a customer's automation is running. The only honest next screen is one re-read
 * from the server, and an optimistic update would be this client's opinion about whether a
 * business process is currently firing.
 *
 * Both directions invalidate the same report, because they move the same task through the
 * same phase — a reactivation that succeeded and a decommission that succeeded are the
 * same row read twice.
 */
export function useEngineDecommission(planId: string | undefined, taskPath: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (confirmation: string) => decommissionEngineTask(planId!, taskPath!, confirmation),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
    },
  });
}

export function useEngineRecommission(planId: string | undefined, taskPath: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (confirmation: string) => recommissionEngineTask(planId!, taskPath!, confirmation),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
    },
  });
}

/** Close a parked apply attempt. Sends nothing to the org; records that someone looked. */
export function useEngineClearApply(planId: string | undefined, taskPath: string | undefined) {
  const queryClient = useQueryClient();
  const ctx = useSkillsRequestContext();
  return useMutation({
    mutationFn: (body: EngineClearApplyRequest) => clearEngineApply(planId!, taskPath!, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: engineKeys(ctx.orgId).report(planId) });
    },
  });
}
